import { type ComponentProps, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "recharts";
import type { ChartTooltipContent } from "@libs/zapi-ui/shadcn";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import type { TFigureRef } from "@/zapi-frontend/shared/types/contentBlocks";
import { BlockCaption } from "./_BlockCaption";
import { BlockLabel } from "./_BlockLabel";
import { BlockTable } from "./_BlockTable";
import { FigureTrigger } from "./FigureRefs/FigureTrigger";
import { memoType } from "./memoTypography";

export const CHART_INK = "var(--innovera-zinc700)";
export const CHART_TRACK_FILL = "var(--innovera-zinc150)";
export const CHART_GRID_STROKE = "var(--innovera-zinc150)";
export const CHART_CURSOR_FILL = "var(--innovera-zinc100)";
export const CHART_AXIS_INK = "var(--innovera-zinc500)";
export const ROW_PITCH = 48;
export const ROW_AXIS_HEIGHT = 30;

export const includeZero = (min: number) => Math.min(min, 0);

export const formatChartNumber = (value: unknown) =>
  typeof value === "number" ? value.toLocaleString("en-US") : String(value ?? "");

export const tooltipRowLabel = (_label: unknown, payload: Array<{ payload?: { label?: string } }>) =>
  payload[0]?.payload?.label ?? "";

// Tooltips quote the block's own display strings ("8.86", "1,180") rather than the browser
// locale's formatting, so a chart never shows "8,86" next to "8.86".
export const tooltipDisplayValue = (dataKey: string, value: unknown, payload?: Record<string, unknown>) => {
  const keyed = payload?.[`${dataKey}Display`];
  if (typeof keyed === "string") return keyed;
  if (dataKey === "value" && typeof payload?.display === "string") return payload.display;
  return formatChartNumber(value);
};

type TTooltipFormatter = NonNullable<ComponentProps<typeof ChartTooltipContent>["formatter"]>;

export const displayValueFormatter: TTooltipFormatter = (value, name, item) => (
  <>
    <div className="h-2.5 w-2.5 shrink-0 rounded-[2px]" style={{ backgroundColor: item.color }} />
    <div className="flex flex-1 items-center justify-between gap-3 leading-none">
      <span className="text-muted-foreground">{name}</span>
      <span className="text-foreground font-mono font-medium tabular-nums">
        {tooltipDisplayValue(String(item.dataKey ?? "value"), value, item.payload)}
      </span>
    </div>
  </>
);

type TChartBlockFrameProps = {
  label?: string | null;
  note?: string | null;
  cols: string[];
  rows: string[][];
  refs?: TFigureRef[];
  skipped?: number;
  children: ReactNode;
};

// Chart blocks share the design's frame: title, the chart itself, a separator, then the
// optional source note and "The Numbers" — the block's full table, collapsed by default.
//
// A stored table keeps every row, and a chart can only draw the rows that hold plain numbers, so
// the frame says how many it left out rather than letting them disappear.
export function ChartBlockFrame({ label, note, cols, rows, refs, skipped = 0, children }: TChartBlockFrameProps) {
  return (
    <figure className="flex min-w-0 flex-col gap-3">
      <BlockLabel label={label} className="" />
      {children}
      <div className="h-px w-full bg-innoveraZinc-150" />
      <BlockCaption caption={note} className="" />
      {skipped > 0 && <SkippedRowsNote count={skipped} />}
      <NumbersDisclosure cols={cols} rows={rows} refs={refs} />
    </figure>
  );
}

function SkippedRowsNote({ count }: { count: number }) {
  const { t } = useTranslation("projectHome");
  return (
    <p className={memoType.note} data-testid="chart-block-skipped">
      {t("chapterBlocks.rowsNotCharted", { count })}
    </p>
  );
}

function NumbersDisclosure({ cols, rows, refs }: { cols: string[]; rows: string[][]; refs?: TFigureRef[] }) {
  const { t } = useTranslation("projectHome");
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className={cn("flex w-fit items-center gap-2", memoType.numbersToggle)}
        data-testid="chart-block-numbers-toggle"
      >
        <svg
          width="9"
          height="8"
          viewBox="0 0 9 8"
          aria-hidden="true"
          className={cn("shrink-0 fill-innoveraZinc-500 transition-transform", isOpen ? "rotate-180" : "rotate-90")}
        >
          <path d="M3.6.5c.385-.667 1.347-.667 1.732 0l3.464 6c.385.667-.096 1.5-.866 1.5H1.002c-.77 0-1.251-.833-.866-1.5L3.6.5Z" />
        </svg>
        {t("chapterBlocks.theNumbers")}
      </button>
      {isOpen && <BlockTable cols={cols} rows={rows} monoFigures={false} refs={refs} />}
    </div>
  );
}

type TBarRow = { label: string; display: string; figureKey?: string };

// Row labels and figures are plain HTML columns beside the chart; the chart's plot area
// starts flush with the first row and is `ROW_PITCH` per row, so the bands line up. The axis
// labels are centred on their grid lines, the outermost on the plot's edges (`interval={0}`
// keeps recharts from nudging them inward), so the SVG must not clip what spills into the gutters.
export function BarRowsLayout({ rows, children }: { rows: TBarRow[]; children: ReactNode }) {
  return (
    <div className="flex gap-4" data-testid="bar-rows">
      <div className="w-[170px] shrink-0">
        {rows.map((row, i) => (
          <div key={i} className={cn("flex items-center", memoType.chartLabel)} style={{ height: ROW_PITCH }}>
            <span className="line-clamp-2">{row.label}</span>
          </div>
        ))}
      </div>
      <div className="min-w-0 flex-1 [&_.recharts-surface]:overflow-visible">{children}</div>
      <div className={cn("min-w-[34px] shrink-0", memoType.chartValue)}>
        {rows.map((row, i) => (
          <div key={i} className="flex items-center justify-end" style={{ height: ROW_PITCH }}>
            <FigureTrigger figureKey={row.figureKey}>{row.display}</FigureTrigger>
          </div>
        ))}
      </div>
    </div>
  );
}

type TMonoAxisTickProps = Omit<ComponentProps<typeof Text>, "children"> & { payload?: { value?: string | number } };

// SVG text paints with `fill`; setting `color` too keeps the inspector's colour readout on the
// design token instead of the inherited body colour.
export function MonoAxisTick({ payload, ...props }: TMonoAxisTickProps) {
  const value = payload?.value;
  return (
    <Text {...props} className={memoType.axisTick} style={{ color: CHART_AXIS_INK, fill: "currentColor" }}>
      {formatChartNumber(value)}
    </Text>
  );
}
